import { servicePath, type ServiceRoute } from '@vnetwork/margarita-shell/service-route';
import type { TokenHost } from './host';
import { tokenPaths } from './paths';

/**
 * Plain data + a lazy loader: do not import the console here (it would join the entry chunk).
 * The host is lazy too: `load` resolves it and registers it before the console's chunk is even
 * requested, so no hook of the module can run before the host exists. `host.ts` is imported here,
 * not at the top, so it stays out of the entry chunk too. The promise is kept, so a second
 * navigation (or a prefetch) does not resolve it again; a failure is not kept.
 *
 * Promise chains, not `async`/`await`: this file is in the entry chunk and the build transpiles
 * `async` into a generator state machine and array destructuring into helpers.
 */
export function createServicesRoutes(loadHost: () => Promise<TokenHost>): ServiceRoute[] {
  let hostReady: Promise<void> | undefined;
  const ready = () => {
    if (!hostReady) {
      hostReady = loadHost()
        .then((host) => import('./host').then((m) => m.registerTokenHost(host)))
        .catch((e) => {
          hostReady = undefined; // a failed chunk fetch must be retryable
          throw e;
        });
    }
    return hostReady;
  };
  return [
    {
      path: servicePath(tokenPaths.base, '*'),
      load: () => ready().then(() => import('./index')).then((m) => ({ default: m.TokenPage })),
    },
  ];
}
